"use client";
import * as React from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, Trash2, MoveLeft } from "lucide-react";
import { useERP, formatBDT, type ID } from "@/lib/erp/store";
import { cn } from "@/lib/utils";

export type FieldType = "text" | "number" | "select" | "date" | "textarea" | "money" | "product-picker" | "customer-picker" | "supplier-picker" | "warehouse-picker" | "line-items";

export interface FormField {
  key: string;
  label: string;
  type: FieldType;
  placeholder?: string;
  options?: { value: string; label: string }[];
  required?: boolean;
  colSpan?: 1 | 2 | 3 | 4;
  defaultValue?: string | number;
  help?: string;
}

export interface LineItemColumn {
  key: string;
  label: string;
  type: "text" | "number" | "money" | "select" | "product-picker";
  options?: { value: string; label: string }[];
  width?: string;
}

export interface FullPageFormProps {
  title: string;
  description?: string;
  fields: FormField[];
  lineItemColumns?: LineItemColumn[];
  lineItemDefault?: Record<string, unknown>;
  onSubmit: (values: Record<string, unknown>, lineItems: Record<string, unknown>[]) => void;
  onBack?: () => void;
  submitLabel?: string;
  defaultLineItemsCount?: number;
  // M11 fix: which product price field to use as default unitPrice ("costPrice" for PO, "salePrice" for SO)
  productPriceField?: "costPrice" | "salePrice";
}

export function FullPageForm({
  title, description, fields, lineItemColumns, lineItemDefault = {}, onSubmit, onBack, submitLabel = "Save", defaultLineItemsCount = 0,
  productPriceField = "costPrice",
}: FullPageFormProps) {
  const erp = useERP();
  const [values, setValues] = React.useState<Record<string, unknown>>(() => {
    const init: Record<string, unknown> = {};
    for (const f of fields) init[f.key] = f.defaultValue ?? (f.type === "number" || f.type === "money" ? 0 : "");
    return init;
  });
  const [items, setItems] = React.useState<Record<string, unknown>[]>(() =>
    Array.from({ length: defaultLineItemsCount }, () => ({ ...lineItemDefault })));

  const setField = (k: string, v: unknown) => setValues(s => ({ ...s, [k]: v }));

  const computeLineTotal = (item: Record<string, unknown>): number => {
    const qty = Number(item.qty ?? 0);
    const up = Number(item.unitPrice ?? 0);
    const disc = Number(item.discount ?? 0);
    return qty * up * (1 - disc / 100);
  };

  const lineTotal = lineItemColumns ? items.reduce((s, it) => s + computeLineTotal(it), 0) : 0;

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSubmit(values, items);
  };

  const renderField = (f: FormField) => {
    const colSpan = f.colSpan === 2 ? "sm:col-span-2" : f.colSpan === 3 ? "sm:col-span-3" : f.colSpan === 4 ? "sm:col-span-4" : "";
    const labelEl = <Label htmlFor={f.key} className="mb-1.5">{f.label}{f.required && <span className="text-destructive"> *</span>}</Label>;
    let control: React.ReactNode;
    if (f.type === "text" || f.type === "date") {
      control = <Input id={f.key} type={f.type === "date" ? "date" : "text"} value={String(values[f.key] ?? "")} placeholder={f.placeholder} onChange={e => setField(f.key, e.target.value)} />;
    } else if (f.type === "number") {
      control = <Input id={f.key} type="number" value={Number(values[f.key] ?? 0)} onChange={e => setField(f.key, Number(e.target.value))} />;
    } else if (f.type === "money") {
      control = <Input id={f.key} type="number" value={Number(values[f.key] ?? 0)} onChange={e => setField(f.key, Number(e.target.value))} className="font-mono" />;
    } else if (f.type === "textarea") {
      control = <Textarea id={f.key} value={String(values[f.key] ?? "")} placeholder={f.placeholder} onChange={e => setField(f.key, e.target.value)} />;
    } else if (f.type === "select" && f.options) {
      control = (
        <Select value={String(values[f.key] ?? "")} onValueChange={v => setField(f.key, v)}>
          <SelectTrigger className="w-full"><SelectValue placeholder={f.placeholder || "Select..."} /></SelectTrigger>
          <SelectContent>{f.options.map(o => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}</SelectContent>
        </Select>
      );
    } else if (f.type === "product-picker") {
      control = (
        <Select value={String(values[f.key] ?? "")} onValueChange={v => setField(f.key, v)}>
          <SelectTrigger className="w-full"><SelectValue placeholder="Select product..." /></SelectTrigger>
          <SelectContent>{erp.products.map(p => <SelectItem key={p.id} value={p.id}>{p.sku} — {p.name}</SelectItem>)}</SelectContent>
        </Select>
      );
    } else if (f.type === "customer-picker") {
      control = (
        <Select value={String(values[f.key] ?? "")} onValueChange={v => setField(f.key, v)}>
          <SelectTrigger className="w-full"><SelectValue placeholder="Select customer..." /></SelectTrigger>
          <SelectContent>{erp.customers.map(c => <SelectItem key={c.id} value={c.id}>{c.code} — {c.name}</SelectItem>)}</SelectContent>
        </Select>
      );
    } else if (f.type === "supplier-picker") {
      control = (
        <Select value={String(values[f.key] ?? "")} onValueChange={v => setField(f.key, v)}>
          <SelectTrigger className="w-full"><SelectValue placeholder="Select supplier..." /></SelectTrigger>
          <SelectContent>{erp.suppliers.map(s => <SelectItem key={s.id} value={s.id}>{s.code} — {s.name}</SelectItem>)}</SelectContent>
        </Select>
      );
    } else if (f.type === "warehouse-picker") {
      control = (
        <Select value={String(values[f.key] ?? "")} onValueChange={v => setField(f.key, v)}>
          <SelectTrigger className="w-full"><SelectValue placeholder="Select warehouse..." /></SelectTrigger>
          <SelectContent>{erp.warehouses.map(w => <SelectItem key={w.id} value={w.id}>{w.code} — {w.name}</SelectItem>)}</SelectContent>
        </Select>
      );
    } else {
      control = <Input id={f.key} value={String(values[f.key] ?? "")} onChange={e => setField(f.key, e.target.value)} />;
    }
    return (
      <div key={f.key} className={cn("flex flex-col", colSpan)}>
        {labelEl}
        {control}
        {f.help && <p className="text-muted-foreground mt-1 text-xs">{f.help}</p>}
      </div>
    );
  };

  return (
    <form onSubmit={handleSubmit} className="flex flex-col gap-6">
      <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-xl font-semibold sm:text-2xl">{title}</h1>
          {description && <p className="text-muted-foreground mt-1 text-sm">{description}</p>}
        </div>
        <div className="flex gap-2">
          {onBack && <Button type="button" variant="outline" onClick={onBack}><MoveLeft className="size-4" /> Back</Button>}
          <Button type="submit">{submitLabel}</Button>
        </div>
      </div>

      <section className="rounded-lg border bg-card p-4 sm:p-6">
        <h2 className="mb-4 text-sm font-semibold uppercase tracking-wide text-muted-foreground">Primary Information</h2>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {fields.map(renderField)}
        </div>
      </section>

      {lineItemColumns && (
        <section className="rounded-lg border bg-card p-4 sm:p-6">
          <div className="mb-4 flex items-center justify-between">
            <h2 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">Line Items</h2>
            <Button type="button" size="sm" variant="outline" onClick={() => setItems([...items, { ...lineItemDefault }])}>
              <Plus className="size-4" /> Add Row
            </Button>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-muted/50">
                <tr className="border-b">
                  {lineItemColumns.map(c => <th key={c.key} className="px-2 py-2 text-left text-xs font-semibold uppercase" style={{ width: c.width }}>{c.label}</th>)}
                  <th className="px-2 py-2 text-right text-xs font-semibold uppercase">Total</th>
                  <th className="px-2 py-2 w-10" />
                </tr>
              </thead>
              <tbody>
                {items.length === 0 ? (
                  <tr><td colSpan={lineItemColumns.length + 2} className="text-muted-foreground px-2 py-6 text-center">No items added. Click "Add Row" to begin.</td></tr>
                ) : items.map((it, idx) => (
                  <tr key={idx} className="border-b last:border-0">
                    {lineItemColumns.map(c => (
                      <td key={c.key} className="px-2 py-1.5">
                        {c.type === "select" && c.options ? (
                          <Select value={String(it[c.key] ?? "")} onValueChange={v => setItems(items.map((x, i) => i === idx ? { ...x, [c.key]: v } : x))}>
                            <SelectTrigger className="h-8 w-full"><SelectValue placeholder="Select..." /></SelectTrigger>
                            <SelectContent>{c.options.map(o => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}</SelectContent>
                          </Select>
                        ) : c.type === "product-picker" ? (
                          <Select value={String(it[c.key] ?? "")} onValueChange={v => {
                            const p = erp.products.find(x => x.id === v);
                            // M11 fix: use productPriceField prop (costPrice for PO, salePrice for SO)
                            const price = p ? ((p as unknown as Record<string, unknown>)[productPriceField] as number ?? p.costPrice) : 0;
                            setItems(items.map((x, i) => i === idx ? { ...x, [c.key]: v, unitPrice: price ?? x.unitPrice, name: p?.name ?? x.name } : x));
                          }}>
                            <SelectTrigger className="h-8 w-full"><SelectValue placeholder="Select..." /></SelectTrigger>
                            <SelectContent>{erp.products.map(p => <SelectItem key={p.id} value={p.id}>{p.sku} — {p.name}</SelectItem>)}</SelectContent>
                          </Select>
                        ) : (
                          <Input type={c.type === "number" || c.type === "money" ? "number" : "text"} value={String(it[c.key] ?? "")}
                            className={cn("h-8", c.type === "money" && "font-mono")}
                            onChange={e => setItems(items.map((x, i) => i === idx ? { ...x, [c.key]: c.type === "number" || c.type === "money" ? Number(e.target.value) : e.target.value } : x))} />
                        )}
                      </td>
                    ))}
                    <td className="px-2 py-1.5 text-right font-mono text-xs">{formatBDT(computeLineTotal(it))}</td>
                    <td className="px-2 py-1.5 text-center">
                      <Button type="button" size="icon" variant="ghost" className="size-7 text-destructive" onClick={() => setItems(items.filter((_, i) => i !== idx))}>
                        <Trash2 className="size-3.5" />
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {items.length > 0 && (
            <div className="mt-4 flex justify-end">
              <div className="w-full sm:w-72">
                <div className="flex justify-between border-t pt-3 text-sm">
                  <span className="text-muted-foreground">Subtotal</span>
                  <span className="font-mono font-semibold">{formatBDT(lineTotal)}</span>
                </div>
                <div className="flex justify-between pt-1 text-sm">
                  <span className="text-muted-foreground">VAT (15%)</span>
                  <span className="font-mono">{formatBDT(lineTotal * 0.15)}</span>
                </div>
                <div className="mt-2 flex justify-between border-t pt-2 text-base font-semibold">
                  <span>Grand Total</span>
                  <span className="font-mono">{formatBDT(lineTotal * 1.15)}</span>
                </div>
              </div>
            </div>
          )}
        </section>
      )}
    </form>
  );
}
